import { OperationEnum, SnackBarVariants, UserRole } from '@app/app.enum';
import store from '@app/app.store';
import { withAxiosCancellation } from '@app/components/core/axios-cancel.component';
import withSnackBar from '@app/components/core/message.component';
import CustomButton from '@app/components/shared/custom-button.component';
import CustomDialogBox from '@app/components/shared/dialog.component';
import Icon from '@app/components/shared/icon.component';
import NoDataComponent from '@app/components/shared/no-data.component';
import { IMenuInjectedProps } from '@app/models/index';
import * as models from '@app/models/index';
import Injector from '@app/services';
import { IntlService } from '@app/services/intl.service';
import { SharedService } from '@app/services/shared.service';
import { UserService } from '@app/services/user.service';
import {
	Badge,
	Button,
	Card,
	CardContent,
	CircularProgress,
	Divider,
	Grid,
	IconButton,
	Input,
	List,
	ListItem,
	ListItemSecondaryAction,
	ListItemText,
	Menu,
	MenuItem,
	Select,
	TextField,
	Toolbar,
	Typography,
} from '@material-ui/core';
import Axios, { CancelTokenSource } from 'axios';
import { Field, Form, Formik, FormikProps, FormikValues } from 'formik';
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state';
import React from 'react';
import { injectIntl } from 'react-intl';
import * as Yup from 'yup';

const intlService: IntlService = Injector.get(IntlService);
const userService: UserService = Injector.get(UserService);
const sharedService: SharedService = Injector.get(SharedService);

const addUserSchema = Yup.object().shape<models.IAddUser>({
	firstName: Yup.string().required(
		intlService.GetFormattedMessage('app.profile.user-info-form.first-name-required-validation')
	),
	lastName: Yup.string().required(
		intlService.GetFormattedMessage('app.profile.user-info-form.last-name-required-validation')
	),
	email: Yup.string()
		.email(intlService.GetFormattedMessage('app.login.signin-email-invalid-validation'))
		.required(intlService.GetFormattedMessage('app.login.signin-email-required-validation')),
	role: Yup.mixed<UserRole>().required(
		intlService.GetFormattedMessage('app.profile.user-info-form.close-account-validation')
	),
});

class UserComponent extends React.Component<models.IAddUserProps, models.IAddUserState> {
	private userIdForDeletion: string;
	private initialAddUserFormValue = models.initAddUser();

	// Separate source required here because of the different forms usage
	private dialogCancellationSource: CancelTokenSource;

	/**
	 * Constructor
	 */
	public constructor(props: models.IAddUserProps) {
		super(props);
		this.state = models.initAddUserState();
	}

	/**
	 * Call after first render
	 */
	public componentDidMount() {
		this.setState({
			loginUserId: store.getState().authentication.user.id,
		});
		this.getUsers();
	}

	public componentWillUnmount() {
		if (!!this.dialogCancellationSource) {
			this.dialogCancellationSource.cancel();
		}
		this.setState({ loading: false });
	}

	public render() {
		/*Filter data based on role */
		const accountAdminRoleUser = this.state.users.filter((x) => x.role === UserRole.AccountAdmin);
		const userRoleUser = this.state.users.filter((x) => x.role === UserRole.User);

		const accountAdmin = (
			<List>
				{accountAdminRoleUser.length > 0 ? accountAdminRoleUser.map((x) => this.getList(x)) : <NoDataComponent />}
			</List>
		);
		const users = (
			<List>{userRoleUser.length > 0 ? userRoleUser.map((x) => this.getList(x)) : <NoDataComponent />}</List>
		);

		this.initialAddUserFormValue = {
			email: this.state.currentUser.email,
			firstName: this.state.currentUser.firstName,
			lastName: this.state.currentUser.lastName,
			role: this.state.currentUser.role,
		};

		return (
			<React.Fragment>
				<Grid container={true}>
					<Grid item={true} xl={8} xs={8} lg={8} md={8} sm={8}>
						<Grid container={true} direction='column' spacing={2}>
							{this.state.showUsersList ? (
								<React.Fragment>
									<Grid item={true}>
										{this.getUsersByRole(
											UserRole.AccountAdmin,
											accountAdmin,
											intlService.GetFormattedMessage('app.user.role-AccountAdmin')
										)}
									</Grid>
									<Grid item={true}>
										{this.getUsersByRole(UserRole.User, users, intlService.GetFormattedMessage('app.user.role-User'))}
									</Grid>
								</React.Fragment>
							) : (
								<Grid item={true}>
									<Toolbar className='panel-heading-light'>
										<Typography variant='h6'>
											{this.state.operation === OperationEnum.Add
												? intlService.GetFormattedMessage('app.user.add-user-label')
												: intlService.GetFormattedMessage('app.user.update-user-label')}
										</Typography>
									</Toolbar>
									<Card>
										<CardContent>{this.addOrEditUserView()}</CardContent>
									</Card>
								</Grid>
							)}
						</Grid>
					</Grid>
				</Grid>
				<CustomDialogBox
					open={this.state.openConfirmationDialog}
					dialogTitle={intlService.GetFormattedMessage('app.user.delete-dialog-title')}
					dialogDescription={intlService.GetFormattedMessage('app.user.delete-dialog-description')}
					closeCustomDialog={this.closeDialog}
				/>
			</React.Fragment>
		);
	}

	private getUsersByRole = (role: UserRole, users: React.ReactNode, toolbarTitle: string): React.ReactNode => {
		return (
			<React.Fragment>
				<Toolbar className='panel-heading-light'>
					<Typography variant='h6'>{toolbarTitle}</Typography>
					<div className='flex-spacer' />
					<IconButton
						onClick={() =>
							this.setState({
								showUsersList: false,
								operation: OperationEnum.Add,
								currentUser: {
									...this.state.currentUser,
									firstName: '',
									lastName: '',
									email: '',
									role,
								},
							})
						}
						color='inherit'
					>
						<Icon name='plus' titleAccess={intlService.GetFormattedMessage('app.user.add-user-label')} />
					</IconButton>
				</Toolbar>
				<Card>
					{this.state.dataLoading ? (
						<div className='spinner-for-panels'>
							<CircularProgress size={32} />
						</div>
					) : (
						<CardContent>{users}</CardContent>
					)}
				</Card>
			</React.Fragment>
		);
	};

	private getList = (user: models.IUser): React.ReactNode => {
		return (
			<React.Fragment key={user.id}>
				<ListItem>
					<Grid container={true} direction='row' spacing={2}>
						<Grid item={true} xl={5} lg={5} md={5} sm={5} xs={5}>
							{this.state.loginUserId === user.id ? (
								<Badge badgeContent='You'>
									<Typography className='pr-2'>
										{user.firstName + ' '}
										{user.lastName ? user.lastName : ''}
									</Typography>
								</Badge>
							) : (
								<Typography className='pr-2'>
									{user.firstName + ' '}
									{user.lastName ? user.lastName : ''}
								</Typography>
							)}
						</Grid>
						<Grid item={true} xl={6} lg={5} md={5} sm={5} xs={5}>
							{user.email}
						</Grid>
					</Grid>
					<ListItemSecondaryAction>
						{this.state.loginUserId !== user.id ? (
							<PopupState variant='popper'>
								{(popupState: IMenuInjectedProps) => (
									<React.Fragment>
										<IconButton {...bindTrigger(popupState)}>
											<Icon name='ellipsis-vertical' className='icon-dimension-square' />
										</IconButton>
										<Menu {...bindMenu(popupState)}>
											{
												<>
													<MenuItem onClick={() => this.openEditUser(user)}>
														{intlService.GetFormattedMessage('app.user.update-label')}
													</MenuItem>
													<MenuItem
														onClick={() => {
															this.userIdForDeletion = user.id;
															this.setState({
																openConfirmationDialog: true,
															});
														}}
													>
														{intlService.GetFormattedMessage('app.user.delete')}
													</MenuItem>
												</>
											}
										</Menu>
									</React.Fragment>
								)}
							</PopupState>
						) : null}
					</ListItemSecondaryAction>
				</ListItem>
				<Divider className='mt-1 mb-1' />
			</React.Fragment>
		);
	};

	private addOrEditUserView = (): React.ReactNode => {
		return (
			<Formik
				initialValues={this.initialAddUserFormValue}
				onSubmit={this.addOrEditUser}
				validationSchema={addUserSchema}
				enableReinitialize={true}
				render={(props: FormikProps<FormikValues>) => (
					<Form noValidate={true}>
						<Grid container={true} direction='row' alignItems='center' spacing={2}>
							{/* First Name */}
							<Grid item={true} xs={12} sm={12} md={12} lg={12} xl={12}>
								<Grid container={true} direction='row' alignItems='center'>
									<Grid item={true} xs={12} md={2} lg={3}>
										<Typography variant='subtitle2' color='inherit' className='required'>
											{intlService.GetFormattedMessage('app.profile-user-info-form.first-name-label')}
										</Typography>
									</Grid>
									<Grid item={true} xs={12} md={10} lg={9}>
										<Field
											name='firstName'
											margin='normal'
											component={TextField}
											type='text'
											fullWidth={true}
											value={props.values.firstName}
											required={true}
											onChange={(e: React.ChangeEvent) => props.handleChange('firstName')(e)}
											error={!!((props.values.firstName.length || props.submitCount) && !!props.errors.firstName)}
											helperText={props.values.firstName.length || props.submitCount ? props.errors.firstName : null}
										/>
									</Grid>
								</Grid>
							</Grid>
							{/* LastName */}
							<Grid item={true} xs={12} sm={12} md={12} lg={12} xl={12}>
								<Grid container={true} direction='row' alignItems='center'>
									<Grid item={true} xs={12} md={2} lg={3}>
										<Typography variant='subtitle2' color='inherit' className='required'>
											{intlService.GetFormattedMessage('app.profile-user-info-form.last-name-label')}
										</Typography>
									</Grid>
									<Grid item={true} xs={12} md={10} lg={9}>
										<Field
											name='lastName'
											margin='normal'
											component={TextField}
											type='text'
											fullWidth={true}
											value={props.values.lastName}
											required={true}
											onChange={(e: React.ChangeEvent) => props.handleChange('lastName')(e)}
											error={!!((props.values.lastName.length || props.submitCount) && !!props.errors.lastName)}
											helperText={props.values.lastName.length || props.submitCount ? props.errors.lastName : null}
										/>
									</Grid>
								</Grid>
							</Grid>

							{/* Email */}
							<Grid item={true} xs={12} sm={12} md={12} lg={12} xl={12}>
								<Grid container={true} direction='row' alignItems='center'>
									<Grid item={true} xs={12} md={2} lg={3}>
										<Typography variant='subtitle2' color='inherit' className='required'>
											{intlService.GetFormattedMessage('app.profile.user-info-form.email-label')}
										</Typography>
									</Grid>
									<Grid item={true} xs={12} md={10} lg={9}>
										<Field
											margin='normal'
											component={TextField}
											type='text'
											fullWidth={true}
											disabled={this.state.operation === OperationEnum.Update}
											onChange={(e: React.ChangeEvent) => props.handleChange('email')(e)}
											value={props.values.email}
											error={!!props.errors.email}
											helperText={props.errors.email}
										/>
									</Grid>
								</Grid>
							</Grid>
							{/* Role */}
							<Grid item={true} xs={12} sm={12} md={12} lg={12} xl={12}>
								<Grid container={true} direction='row' alignItems='center'>
									<Grid item={true} xs={12} md={2} lg={3}>
										<Typography variant='subtitle2' color='inherit' className='required'>
											{intlService.GetFormattedMessage('app.user.role-label')}
										</Typography>
									</Grid>
									<Grid item={true} xs={12} md={10} lg={9}>
										<Select
											fullWidth={true}
											name='role'
											input={<Input />}
											value={props.values.role}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) => props.handleChange('role')(e)}
											renderValue={(selected) => intlService.GetFormattedMessage('app.user.role-' + selected)}
											disabled={this.state.operation === OperationEnum.Add}
										>
											<MenuItem key={UserRole.AccountAdmin} value={UserRole.AccountAdmin}>
												<ListItemText primary={intlService.GetFormattedMessage('app.user.role-AccountAdmin')} />
											</MenuItem>
											<MenuItem key={UserRole.User} value={UserRole.User}>
												<ListItemText primary={intlService.GetFormattedMessage('app.user.role-User')} />
											</MenuItem>
										</Select>
									</Grid>
								</Grid>
							</Grid>
							{/* save user info button */}
							<Grid container={true} direction='row' alignItems='center' justifyContent='center' className='mt-2 mb-2'>
								{/* Button Component */}
								<Grid item={true}>
									<CustomButton
										disabled={this.state.loading}
										buttonText={intlService.GetFormattedMessage('app.user.save-button')}
										loading={this.state.loading}
									/>
								</Grid>
								<Grid className='ml-2' item={true}>
									<Button
										variant='outlined'
										onClick={() => {
											if (!!this.dialogCancellationSource) {
												this.dialogCancellationSource.cancel();
											}
											this.setState({
												showUsersList: true,
												loading: false,
											});
											this.dialogCancellationSource = Axios.CancelToken.source();
										}}
									>
										{intlService.GetFormattedMessage('app.user.cancel-button')}
									</Button>
								</Grid>
							</Grid>
						</Grid>
					</Form>
				)}
			/>
		);
	};

	/**
	 * Get Users
	 */
	private getUsers = async () => {
		try {
			const res = await userService.getUsers(this.props.axiosCancellationToken);
			this.setState({
				users: res,
				dataLoading: false,
			});
		} catch (error) {
			if (!Axios.isCancel(error)) {
				this.props.openSnackBar(sharedService.parseError(error.data), SnackBarVariants.Error);
			}
		}
	};

	/**
	 * Add or edit User
	 */
	private addOrEditUser = async (user: models.IAddUser) => {
		this.setState({ loading: true });
		this.dialogCancellationSource = Axios.CancelToken.source();
		if (this.state.operation === OperationEnum.Add) {
			await userService
				.addUser(user, this.dialogCancellationSource.token)
				.then((res) => {
					this.setState({
						users: [...this.state.users, ...[res]],
						showUsersList: true,
						loading: false,
					});
				})
				.catch((err) => {
					if (!Axios.isCancel(err)) {
						this.openSnackbar(sharedService.parseError(err.data), SnackBarVariants.Error);
					}
				});
		} else {
			await userService
				.updateUser(this.state.currentUser.id, user, this.dialogCancellationSource.token)
				.then((res) => {
					this.state.users.splice(
						this.state.users.findIndex((x) => x.id === this.state.currentUser.id),
						1,
						res
					);
					this.setState({
						users: this.state.users,
						showUsersList: true,
						loading: false,
					});
				})
				.catch((err) => {
					if (!Axios.isCancel(err)) {
						this.openSnackbar(sharedService.parseError(err.data), SnackBarVariants.Error);
					}
				});
		}
	};

	/**
	 * Update User
	 */
	private openEditUser = async (user: models.IUser) => {
		this.setState({
			currentUser: user,
			showUsersList: false,
			operation: OperationEnum.Update,
		});
	};

	/**
	 * Delete User
	 */
	private deleteUser = async (userId: string) => {
		try {
			const res = await userService.deleteUser(userId, this.dialogCancellationSource.token);
			if (res) {
				this.state.users.splice(
					this.state.users.findIndex((x) => x.id === userId),
					1
				);
			}
			this.setState({
				users: this.state.users,
			});
		} catch (e) {
			if (!Axios.isCancel(e)) {
				throw e;
			}
		}
	};

	// open  snackbar
	private openSnackbar = (message: string | string[], variantName: SnackBarVariants) => {
		this.setState({
			loading: false,
		});
		this.props.openSnackBar(message, variantName);
	};

	private closeDialog = (dialogTitle: string, isCancel: boolean) => {
		if (!isCancel) {
			this.deleteUser(this.userIdForDeletion);
			isCancel = true;
		}
		this.userIdForDeletion = '';
		this.setState({
			openConfirmationDialog: false,
		});
	};
}

export default withAxiosCancellation(withSnackBar(injectIntl(UserComponent)));



// WEBPACK FOOTER //
// ./src/components/admin/users/admin-users.component.tsx